import React, { useState } from "react";
import { INK, MUTED, PAPER, GRID, W, M, axis, scale, fmt, signed, plural, lowerFirst, clusterColor, spread, Legend, Tooltip, Axes } from "./clusterKit";

// ---- cluster_profiles: each cluster's mean on each clustering variable, in z-scores ----
// Props (ChartData#cluster_profiles): variableNames [names], clusters [{ name, n, means [z per variable], raw [raw mean per variable] }],
// overall [whole-sample mean per variable], sds [whole-sample SD per variable], legendTitle, noun.
export default function ClusterProfilesChart({ variableNames, clusters, overall, sds, legendTitle, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const k = variableNames.length;
	const height = 300, box = { l: 44, r: W - 148, t: M.top + 22, b: height - 40 };
	const axY = axis([0, ...clusters.flatMap((c) => c.means)], 0.1);
	const y = scale(axY, box.b, box.t);
	const x = (j) => box.l + 40 + (k === 1 ? 0 : (j / (k - 1)) * (box.r - box.l - 80));
	const ends = spread(clusters.map((c, i) => ({ i, y: y(c.means[k - 1]) })), 30);
	const dot = (c, i, j) => ({
		i, cx: x(j),
		lines: [`${c.name} (${plural(c.n, noun)})`, `${variableNames[j]}: ${signed(c.means[j])} SD from the average`,
			`Mean ${fmt(c.raw[j])}, against ${fmt(overall[j])} for everyone (SD ${fmt(sds[j])})`]
	});
	return (
		<figure>
			<Legend title={legendTitle} items={[...clusters.map((c, i) => ({ label: c.name, color: clusterColor(i), kind: "line" })),
				{ label: `Average of all ${plural(clusters.reduce((a, c) => a + c.n, 0), noun)}`, color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Profile of each cluster's mean on ${k} measures, in standard deviations from the whole sample's average. ${clusters.map((c) => `${c.name}: ${variableNames.map((v, j) => `${lowerFirst(v)} ${signed(c.means[j])}`).join(", ")}`).join("; ")}.`}>
				<Axes axX={{ ticks: [] }} axY={axY} x={x} y={y} box={box} yLabel="Cluster mean, in SDs above (+) or below (−) everyone's average" />
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={MUTED} strokeWidth="1.5" />
				{variableNames.map((v, j) => (
					<g key={v}>
						<line x1={x(j)} x2={x(j)} y1={box.t} y2={box.b} stroke={GRID} strokeDasharray="2 3" />
						<text x={x(j)} y={box.b + 18} fontSize="11" fill={INK} textAnchor="middle">{v}</text>
					</g>
				))}
				{clusters.map((c, i) => {
					const dim = hover && hover.i !== i;
					return (
						<g key={c.name} opacity={dim ? 0.3 : 1}>
							<polyline points={c.means.map((m, j) => `${x(j)},${y(m)}`).join(" ")} fill="none" stroke={clusterColor(i)} strokeWidth="2.5" strokeLinejoin="round" />
							{c.means.map((m, j) => (
								<circle key={j} cx={x(j)} cy={y(m)} r="5" fill={clusterColor(i)} stroke={PAPER} strokeWidth="1.5"
									onMouseEnter={() => setHover(dot(c, i, j))} onMouseLeave={() => setHover(null)} />
							))}
						</g>
					);
				})}
				{clusters.map((c, i) => (
					<g key={`e${i}`}>
						<text x={box.r + 8} y={ends[i].y - 2} fontSize="12" fill={INK} fontWeight="600">{c.name}</text>
						<text x={box.r + 8} y={ends[i].y + 12} fontSize="11" fill={MUTED}>{plural(c.n, noun)}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx - 120} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each line is one cluster's center: its members' mean on each measure, as a z-score (0 is the whole sample's average, and 1 is one standard deviation above it). The shape of the line is the cluster's profile; a point far from 0 is what sets that cluster apart.
			</figcaption>
		</figure>
	);
}
